// AS TRÊS PÁGINAS QUE O RODAPÉ PROMETIA E NÃO EXISTIAM.
//
// T2.12 do pedido, e ele é literal: *"não sei onde acessar essas informações para começo de
// conversa. Se eu quiser checar LGPD disso aqui, NÃO TENHO COMO CHECAR."* Medido em 2026-09-12:
// não havia rota de LGPD, de privacidade nem de termos em lugar nenhum do produto.
//
// ── DE ONDE VEIO O TEXTO, E POR QUE NÃO FUI EU QUE O INVENTEI ────────────────────────────────
//
// `T2.14`: *"pode usar as outras plataformas como estão agora, com as alterações que a gente pediu
// DE MODELO para alterar esse rodapé, colocar essas outras informações."*
//
// O modelo é o `educahubplay/app/legal/{lgpd,privacidade,termos}`, escrito em 2026-09-09 pelo lead
// daquele ticket a pedido do operador, e o `xequemath/app/components/rodape.tsx` aponta para ele
// como referência. A ESTRUTURA é aquela: bases legais, direitos do titular, artigo 14, canal do
// encarregado.
//
// ── O QUE EU MUDEI, E ESSA É A PARTE QUE IMPORTA ─────────────────────────────────────────────
//
// Os FATOS são outros, e copiar fato de outro produto seria mentir com texto emprestado. O
// EducaHubPlay descreve um fluxo de autorização do responsável por e-mail, com link próprio. **O
// HQ-Lab não tem isso.** O que ele tem é portão etário AUTODECLARADO, com piso de 6 anos
// (`api/idade.ts`), e nenhuma verificação nem autorização de responsável.
//
// Então esta página diz isso — inclusive onde é lacuna. Uma página de LGPD que descreve a prática
// do vizinho é pior que a ausência dela: dá aparência de conformidade e some com a única coisa
// útil, que é saber o que de fato acontece.
//
// E o aviso de rascunho pendente de revisão jurídica FICA. Eu não sou advogado, o operador não
// pediu que eu fosse, e um texto legal sem esse aviso afirma uma revisão que não houve.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "../../components/nav";
import { IDADE_MINIMA } from "../../api/idade";

import "./legal.scss";

export type SecaoLegal = "lgpd" | "privacidade" | "termos";

const TITULOS: Record<SecaoLegal, string> = {
  lgpd: "LGPD — Lei Geral de Proteção de Dados",
  privacidade: "Política de privacidade",
  termos: "Termos de uso",
};

// 06/10/2026 · e-mail do encarregado definido pelo operador em chat (era o gmail da MVP).
const CANAL = "contato.eleva@outlook.com.br";
const ATUALIZADO = "6 de outubro de 2026";

// 06/10/2026 · O selo «Rascunho» saiu. O operador autorizou o conteúdo jurídico em chat:
// «Considere autorizado, se precisarmos de revisões futuras eu reabro esse ponto».

/** Os links entre as três, no pé de cada uma. */
function VerTambem({ atual }: { atual: SecaoLegal }) {
  const outras = (Object.keys(TITULOS) as SecaoLegal[]).filter((s) => s !== atual);
  return (
    <p className="legal-ver-tambem">
      Ver também:{" "}
      {outras.map((s, i) => (
        <span key={s}>
          {i > 0 && " · "}
          <Link to={`/${s}`}>{TITULOS[s]}</Link>
        </span>
      ))}
    </p>
  );
}

function Lgpd() {
  return (
    <>
      <p>
        O HQ-Lab é um projeto do <strong>GEOTEC</strong>, na Universidade do Estado da Bahia
        (UNEB), e opera em conformidade com a{" "}
        <strong>Lei Geral de Proteção de Dados Pessoais (Lei 13.709/2018)</strong>.
      </p>

      <h2>Bases legais utilizadas</h2>
      <ul>
        <li>
          <strong>Consentimento</strong> (Art. 7º, I) — cadastro e uso da plataforma
        </li>
        <li>
          <strong>Legítimo interesse</strong> (Art. 7º, IX) — segurança e funcionamento do serviço
        </li>
        <li>
          <strong>Cumprimento de obrigação legal</strong> (Art. 7º, II) — quando aplicável
        </li>
      </ul>

      <h2>Direitos do titular (Art. 18)</h2>
      <ul>
        <li>Confirmação da existência de tratamento</li>
        <li>Acesso aos dados</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados</li>
        <li>Anonimização, bloqueio ou eliminação</li>
        <li>Portabilidade</li>
        <li>Revogação do consentimento</li>
      </ul>
      <p>
        Os dados de cadastro são visíveis e editáveis pela própria pessoa em <em>Minha conta</em>.
        Para os demais direitos, use o canal no fim desta página.
      </p>

      <h2>Crianças e adolescentes (Art. 14)</h2>
      <p>
        O HQ-Lab é uma ferramenta escolar de criação de quadrinhos, e isso significa que parte das
        pessoas cadastradas tem <strong>menos de 18 anos</strong>. A LGPD trata o dado de criança e
        adolescente em artigo próprio e exige que o tratamento seja feito no{" "}
        <strong>melhor interesse</strong> delas, com consentimento específico e destacado dado por
        ao menos um dos pais ou responsável legal.
      </p>

      <p>
        <strong>O que a plataforma faz hoje, e é pouco:</strong>
      </p>
      <ul>
        <li>
          O cadastro pede a data de nascimento, mostra a idade calculada em voz alta antes de
          continuar, e recusa idade abaixo de {IDADE_MINIMA} anos — que é a idade de entrada no
          ensino fundamental.
        </li>
        <li>
          Coletamos o mínimo: nome, e-mail, cidade, escola e data de nascimento. Não pedimos
          endereço, telefone, documento nem localização.
        </li>
        <li>
          O que a criança cria — quadrinhos, imagens e textos — fica na conta dela e não aparece
          para mais ninguém enquanto ela não publicar.
        </li>
      </ul>

      <p>
        <strong>E o que a plataforma ainda NÃO faz.</strong> Dizemos isto porque omitir seria pior
        do que admitir:
      </p>
      <ul>
        <li>
          A idade é <strong>autodeclarada</strong> e não há verificação. Quem digitar outra data
          passa.
        </li>
        <li>
          <strong>Não existe, hoje, um fluxo de autorização do responsável.</strong> Não pedimos
          e-mail de pai, mãe ou responsável, e não enviamos pedido de autorização. Esta é uma
          lacuna conhecida e está registrada como pendência do produto.
        </li>
      </ul>
      <p>
        Enquanto essa lacuna existir, o uso do HQ-Lab por criança deve acontecer{" "}
        <strong>sob acompanhamento da escola ou de um responsável</strong>, que é o contexto para o
        qual a ferramenta foi feita.
      </p>
      <p>
        Não usamos dado de criança ou adolescente para publicidade, não repassamos a terceiros para
        fins comerciais, e não condicionamos o uso da plataforma ao fornecimento de dado além do
        necessário.
      </p>

      <h2>Encarregado (DPO) e canal</h2>
      <p>
        Para exercer qualquer direito previsto na LGPD, pedir a exclusão de uma conta ou reportar
        incidente: <a href={`mailto:${CANAL}`}>{CANAL}</a>
      </p>
    </>
  );
}

function Privacidade() {
  return (
    <>
      <h2>O que guardamos</h2>
      <ul>
        <li>
          <strong>Do cadastro:</strong> nome, e-mail, cidade, escola e data de nascimento.
        </li>
        <li>
          <strong>Do seu trabalho:</strong> os quadrinhos que você salva, as imagens que você envia
          e as imagens que a plataforma gera para você, com o texto que você pediu.
        </li>
        <li>
          <strong>Do que você publica:</strong> quando você marca um quadrinho como público, o
          título, o seu nome, a turma e a quantidade de páginas passam a ser visíveis; as páginas em
          si exigem conta para serem lidas.
        </li>
        <li>
          <strong>Do que você nos manda pelo botão de comentário:</strong> o texto, o seu e-mail e
          qualquer arquivo anexado — foto, captura de tela ou áudio.
        </li>
      </ul>

      <h2>Com quem esses dados são compartilhados</h2>
      <p>
        <strong>Isto é o mais importante desta página, e costuma não ser dito em lugar nenhum:</strong>{" "}
        quando você pede um desenho, <strong>o texto do seu pedido é enviado para um serviço de
        geração de imagem de outra empresa</strong> — é ele que desenha. O seu nome, o seu e-mail e
        a sua idade <strong>não</strong> vão com o pedido; vai o texto do desenho.
      </p>
      <p>
        Além disso, usamos o <strong>Firebase</strong>, do Google, para guardar a conta e os
        quadrinhos, e um servidor próprio do projeto para receber os comentários enviados pelo botão
        de comentário.
      </p>
      <p>Não vendemos dado nenhum, e não há publicidade na plataforma.</p>

      <h2>Por quanto tempo</h2>
      <p>
        Enquanto a conta existir. Pedindo a exclusão pelo canal abaixo, apagamos o cadastro e os
        quadrinhos. O que já foi publicado na biblioteca também sai.
      </p>

      <h2>Canal</h2>
      <p>
        <a href={`mailto:${CANAL}`}>{CANAL}</a>
      </p>
    </>
  );
}

function Termos() {
  return (
    <>
      <h2>O que é o HQ-Lab</h2>
      <p>
        Uma ferramenta para criar histórias em quadrinhos em sala de aula, feita pelo GEOTEC na
        Universidade do Estado da Bahia (UNEB). O uso é gratuito.
      </p>

      <h2>Quem pode usar</h2>
      <p>
        Quem tem {IDADE_MINIMA} anos ou mais. Sendo menor de idade, o uso deve acontecer com
        acompanhamento da escola ou de um responsável.
      </p>

      <h2>O que você cria é seu</h2>
      <p>
        As histórias e os desenhos que você faz continuam seus. Ao marcar um quadrinho como{" "}
        <strong>público</strong>, você autoriza que outras pessoas da plataforma o leiam na
        biblioteca — e pode voltar atrás, deixando de publicar.
      </p>

      <h2>O que não é permitido</h2>
      <ul>
        <li>Conteúdo de ódio, ofensa ou ameaça a qualquer pessoa ou grupo.</li>
        <li>Conteúdo sexual, violento ou impróprio para o público escolar.</li>
        <li>Usar a conta de outra pessoa, ou criar conta em nome de outra pessoa.</li>
        <li>Tentar burlar as barreiras de conteúdo da plataforma.</li>
      </ul>
      <p>
        A plataforma tem barreiras automáticas, e elas não pegam tudo. Encontrando algo impróprio,
        avise pelo botão de comentário ou pelo canal abaixo.
      </p>

      <h2>Os desenhos gerados pela plataforma</h2>
      <p>
        Os desenhos são feitos por um serviço de geração de imagem de outra empresa, a partir do
        texto que você escreve. O resultado pode não corresponder ao que você pediu, e pode falhar.
        Quando falha, a plataforma avisa em vez de mostrar um quadro vazio sem explicação.
      </p>

      <h2>Sem garantia de disponibilidade</h2>
      <p>
        É um projeto de pesquisa e extensão universitária. Pode sair do ar para manutenção. Salve o
        seu trabalho.
      </p>

      <h2>Canal</h2>
      <p>
        <a href={`mailto:${CANAL}`}>{CANAL}</a>
      </p>
    </>
  );
}

const CORPOS: Record<SecaoLegal, () => JSX.Element> = {
  lgpd: Lgpd,
  privacidade: Privacidade,
  termos: Termos,
};

function Legal({ secao }: { secao: SecaoLegal }) {
  useEffect(() => {
    // Título de aba próprio. Três rotas, três títulos — é o que faz o histórico do navegador
    // dizer alguma coisa depois de a pessoa visitar as três.
    document.title = `${TITULOS[secao]} · HQ-Lab`;
    return () => {
      document.title = "HQ-Lab";
    };
  }, [secao]);

  const Corpo = CORPOS[secao];

  return (
    <div className="legal-container">
      <Nav />

      <main className="legal-conteudo">
        <h1>{TITULOS[secao]}</h1>
        <p className="legal-data">Última atualização: {ATUALIZADO}</p>


        <section className="legal-corpo">
          <Corpo />
          <VerTambem atual={secao} />
        </section>
      </main>
    </div>
  );
}

export default Legal;
